import React, { memo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./ProductCard.css";
import { FaHeart, FaRegHeart } from "react-icons/fa6";
import { colors } from "../../../../constants/colors";
import {
  getPrice,
  getErrorMessage,
  getOptimizedUrl,
} from "../../../../constants/globalFunction";
import { getColorCount } from "../../../../utils/productInfo";
import { appApiHandler } from "../../../../api/appApiHandler";
import { setWhishlist } from "../../../../store/slices/whishlistState";
import { showErrorToast } from "../../../../utils/toast";
import { string } from "../../../../constants/string";
import { setWhishlistCount } from "../../../../store/slices/countState";
import { RUPEE } from "../../../../constants/globalConstants";

const toNumber = (value) => {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? 0 : n;
};

const getPricingSizeObj = (item) => {
  const s = item?.size;
  if (s == null) return null;
  if (Array.isArray(s)) {
    if (s.length === 0) return null;
    const withSellPrice = s.find(
      (x) =>
        x?.retail_price != null ||
        x?.wholesale_price != null ||
        x?.price != null,
    );
    return withSellPrice ?? s[0];
  }
  return s;
};

const getMrp = (item) => {
  const variant = getPricingSizeObj(item) || item?.size?.[0] || {};
  return (
    toNumber(item?.mrp) ||
    toNumber(item?.original_price) ||
    toNumber(item?.compare_at_price) ||
    toNumber(variant?.mrp) ||
    toNumber(variant?.original_price) ||
    toNumber(variant?.compare_at_price) ||
    0
  );
};

const getIsSoldOut = (item) => {
  if (item?.is_sold_out === true || item?.sold_out === true) return true;
  if (item?.in_stock === false || item?.is_in_stock === false) return true;
  if (typeof item?.stock === "number" && item.stock <= 0) return true;
  if (Array.isArray(item?.size) && item.size.length > 0) {
    const everyOut = item.size.every((s) => {
      const stockVal = s?.stock ?? s?.quantity ?? s?.qty;
      if (typeof stockVal === "number") return stockVal <= 0;
      if (s?.in_stock === false || s?.is_in_stock === false) return true;
      return false;
    });
    if (everyOut) return true;
  }
  return false;
};

const ProductCard = ({ item, variant = "grid", singleImage = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const { userData } = useSelector((state) => state.userState);
  const { whishlist } = useSelector((state) => state.whishlistState);

  const isWishlisted = whishlist?.some(
    (wishItem) => wishItem?.product_id === item?._id,
  );

  const refreshWishlist = async () => {
    const response = await appApiHandler.getWishlist({
      user_id: userData?._id,
    });
    if (response?.status === 200 && response?.data) {
      dispatch(setWhishlist(response?.data));
      dispatch(setWhishlistCount(response?.data?.length));
    }
  };

  const onPress = () => {
    const productId = item?._id ?? item?.id;
    if (productId == null || String(productId).trim() === "") return;
    navigate({
      pathname: "/product",
      search: `?product_id=${encodeURIComponent(String(productId))}`,
      state: { product_id: productId },
    });
  };

  const onToggleWishlist = async () => {
    if (!userData) {
      showErrorToast(string.error.please_login_to_your_account);
      return;
    }

    const reqData = {
      user_id: userData?._id,
      product_id: item?._id,
    };

    setWishlistLoading(true);
    const response = isWishlisted
      ? await appApiHandler.removeFromWishlist(reqData)
      : await appApiHandler.addToWishlist(reqData);
    setWishlistLoading(false);

    if (response?.status === 200) {
      refreshWishlist();
    } else if (response?.message) {
      showErrorToast(getErrorMessage(response?.message));
    } else {
      showErrorToast(string.error.failed_due_to_internal_error);
    }
  };

  const pricingSizeRow = getPricingSizeObj(item);
  const currentPrice = getPrice(pricingSizeRow);
  const mrp = getMrp(item);
  const hasDiscount = mrp > currentPrice && currentPrice > 0;
  const discountPercent = hasDiscount
    ? Math.round(((mrp - currentPrice) / mrp) * 100)
    : 0;
  const colorCount = getColorCount(item);
  const isSoldOut = getIsSoldOut(item);

  const primaryImage = item?.image?.[0]?.image;

  //const hoverImage = singleImage ? null : item?.image?.[1]?.image;
  const hoverImage = item?.image?.[1]?.image;
  //console.log("singleImage:", singleImage);

  // console.log("Item:", item);
  //console.log("Images:", item?.image);
  // console.log("Primary img:", primaryImage);
  // console.log("Hover img:", hoverImage);

  const rootClass = [
    "productCard",
    variant === "strip" && "productCard--strip",
    singleImage && "productCard--singleImage",
    isSoldOut && "productCard--sold-out",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={rootClass}
      onClick={onPress}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPress();
        }
      }}
      // onMouseEnter={() =>
      //   !singleImage && item?.image?.length > 1 && setIsHovered(true)
      // }

      onMouseEnter={() => item?.image?.length > 1 && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* ── Ribbon ── */}
      {isSoldOut ? (
        <span className="productCard__ribbon productCard__ribbon--sold-out">
          Sold Out
        </span>
      ) : (
        hasDiscount && (
          <span className="productCard__ribbon">{discountPercent}% OFF</span>
        )
      )}

      {/* ── Wishlist Button ── */}
      <button
        type="button"
        className={`productCard__wish ${isWishlisted ? "productCard__wish--wishlisted" : ""
          }`}
        disabled={wishlistLoading}
        aria-pressed={!!isWishlisted}
        onClick={(e) => {
          e.stopPropagation();
          onToggleWishlist();
        }}
      >
        {isWishlisted ? (
          <FaHeart size={variant === "strip" ? 14 : 15} color={colors.red} />
        ) : (
          <FaRegHeart
            size={variant === "strip" ? 14 : 15}
            color={colors.primary}
          />
        )}
      </button>
      {/* console.log("second image ",item.image); */}
      {/* ── Image Container ── */}
      <div className="productCard__image-container">
        {primaryImage ? (
          <>
            {/* Primary image: hidden when hovered AND hover image exists */}
            <img
              className={`productCard__img ${isHovered && hoverImage ? "productCard__img--hidden" : ""
                }`}
              src={getOptimizedUrl(primaryImage)}
              alt={item?.name || "product"}
              loading="lazy"
            />

            {/* Hover image: only rendered if it exists, shown when isHovered */}
            {hoverImage && (
              <img
                className={`productCard__img productCard__img--hover ${isHovered ? "productCard__img--visible" : ""
                  }`}
                src={getOptimizedUrl(hoverImage)}
                alt={item?.name || "product"}
                loading="lazy"
              />
            )}
          </>
        ) : (
          <div className="productCard__img productCard__img--placeholder" />
        )}

        {/* Sold out overlay on image */}
        {isSoldOut && (
          <div className="productCard__sold-out-overlay">
            <span>Sold Out</span>
          </div>
        )}
      </div>

      {/* ── Text Content ── */}
      <div className="productCard__content">
        <div className="productCard__name" title={item?.name}>
          {item?.name}
        </div>

        <div className="productCard__price-row">
          <span className="productCard__priceLeading">
            {RUPEE}
            {currentPrice}
          </span>
        </div>

        {hasDiscount && (
          <div className="productCard__mrp-row">
            <span className="productCard__mrp">
              {RUPEE}
              {mrp}
            </span>
            <span className="productCard__discount-text">
              ({discountPercent}% off)
            </span>
          </div>
        )}

        {colorCount > 0 && (
          <div className="productCard__colors-tag">
            {colorCount} {colorCount === 1 ? "Color" : "Colors"}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(ProductCard);
